import { ApiError } from "@softmax/observatory-client/transport";
import type {
  DivisionLeaderboardSummaryPublic,
  LeaguePublic,
  RoundListPublic,
  Schemas,
} from "@softmax/observatory-client/types";

import type { PlayTransport } from "./play-transport";
export type DivisionLeaderboardRead =
  | {
      status: "published";
      summary: DivisionLeaderboardSummaryPublic;
    }
  | {
      status: "unpublished";
    }
  | {
      status: "unavailable";
    };
type RoundStatus = Schemas["RoundStatus"];
export function createPlayLeagueReads(transport: PlayTransport) {
  const { playFetch, playFetchWithHeaders } = transport;
  const league = (id: string): Promise<LeaguePublic | null> => {
    return playFetch<LeaguePublic>({
      path: `/v2/leagues/${encodeURIComponent(id)}`,
    });
  };
  const coworld = (
    id: string,
  ): Promise<Schemas["CoworldUploadResponse"] | null> =>
    playFetch<Schemas["CoworldUploadResponse"]>({
      path: `/v2/coworlds/${encodeURIComponent(id)}`,
    });
  const rounds = ({
    leagueId,
    divisionId,
    status,
    limit,
    cursor,
    anonymous = false,
  }: {
    leagueId?: string;
    divisionId?: string;
    status?: RoundStatus;
    limit?: number;
    cursor?: string;
    anonymous?: boolean;
  }): Promise<RoundListPublic | null> => {
    const query = new URLSearchParams();

    if (leagueId) query.set("league_id", leagueId);

    if (divisionId) query.set("division_id", divisionId);

    if (status) query.set("status", status);

    if (limit !== undefined) query.set("limit", String(limit));

    if (cursor) query.set("cursor", cursor);

    return playFetch<RoundListPublic>({
      path: `/v2/rounds?${query.toString()}`,
      anonymous,
    });
  };
  const divisionLeaderboard = async ({
    divisionId,
    anonymous = false,
  }: {
    divisionId: string;
    anonymous?: boolean;
  }): Promise<DivisionLeaderboardRead> => {
    const path = `/v2/divisions/${encodeURIComponent(divisionId)}/leaderboard/summary`;
    const read =
      await playFetchWithHeaders<DivisionLeaderboardSummaryPublic | null>({
        path: path,
        anonymous,
      }).catch((error: unknown) => {
        if (
          error instanceof ApiError &&
          (error.status === 401 || error.status === 403)
        )
          throw error;

        console.error(`[play] GET ${path} failed`, error);
        return null;
      });

    if (read === null) return { status: "unavailable" };

    if (read.body === null) return { status: "unpublished" };

    return { status: "published", summary: read.body };
  };
  const leagueIsPublic = async (leagueId: string) => {
    const l = await league(leagueId);
    return l !== null && l.public === true;
  };
  return { league, coworld, rounds, divisionLeaderboard, leagueIsPublic };
}
